import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import BuildOutlined from '@mui/icons-material/BuildOutlined';
import AddOutlined from '@mui/icons-material/AddOutlined';

export default function OemJobSetupDialog({ open, onClose, onSaved, organizationId }) {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [createMode, setCreateMode] = useState(false);
  const [newJobName, setNewJobName] = useState(() => t('oem.defaultJobName'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    fetchHourlyJobs();
  }, [open]);

  const fetchHourlyJobs = async () => {
    setLoading(true);
    try {
      const resp = await axios.get('/api/jobs/', {
        params: { organization: organizationId, page_size: 100 },
      });
      const data = resp.data.results || resp.data;
      const hourly = data.filter(j => j.price_type === 'hourly' && !j.is_deleted);
      setJobs(hourly);
    } catch (err) {
      // An empty list here reads as "this workshop has no hourly job templates"
      // and steers the admin into creating a duplicate — surface the failure.
      setJobs([]);
      setError(t('oem.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let jobId;
      if (createMode) {
        const resp = await axios.post('/api/jobs/', {
          organization: organizationId,
          name: newJobName,
          price_type: 'hourly',
          price: 0,
        });
        jobId = resp.data.id;
      } else {
        jobId = selectedJob?.id;
      }

      if (!jobId) return;

      await axios.patch(`/api/organizations/${organizationId}/`, {
        default_oem_service_job: jobId,
      });

      onSaved?.(jobId);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || t('oem.fetchError'));
    } finally {
      setSaving(false);
    }
  };

  const canSave = createMode ? newJobName.trim() : selectedJob;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: 'rgba(49,130,206,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BuildOutlined sx={{ fontSize: 28, color: 'secondary.main' }} />
          </Box>
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
          {t('oem.jobSetupTitle')}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
          {t('oem.jobSetupDescription')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        {error && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fee2e2', borderRadius: '8px', color: '#dc2626', fontSize: 13 }}>
            {error}
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : !createMode ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={jobs}
              getOptionLabel={(opt) => opt.name || ''}
              value={selectedJob}
              onChange={(_, val) => setSelectedJob(val)}
              renderInput={(params) => (
                <TextField {...params} autoComplete="off" label={t('oem.selectExistingJob')} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
              )}
            />
            <Button
              startIcon={<AddOutlined />}
              onClick={() => setCreateMode(true)}
              sx={{ textTransform: 'none', color: 'secondary.main' }}
            >
              {t('oem.createNewJob')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={t('oem.newJobName')}
              value={newJobName}
              onChange={(e) => setNewJobName(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <Button onClick={() => setCreateMode(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              {t('oem.backToExisting')}
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, gap: 1, justifyContent: 'center' }}>
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: '10px', px: 3 }}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || saving}
          sx={{ borderRadius: '10px', px: 3, bgcolor: 'secondary.main' }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
